import { CognitoUser } from "amazon-cognito-identity-js";
import { useAsyncExecutor } from "async-executor-hook";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { generateSequentialArray } from "../../common/utils";

import LoadingSpinner from "../../images/icon_loading_green.gif";
import TalmaLogo from "../../images/talma_logo.png";
import { USER_POOL } from "../../utils/authUtils";
import { AUTHENTICATION_STEPS } from "../../utils/constants";
import { NUMBER_DIGITS_FORGOT_PASSWORD_CODE } from "../../utils/valueStaticConstants";
import Input from "../Input/Input";
import {
  Container,
  EmailStep,
  MainButton,
  NewPasswordStep,
  StepContainer,
} from "./ForgotPasswordStyles";

const VALIDATIONS = {
  EMAIL: {
    pattern: /^.+@.+$/,
    message: "Correo inválido",
  },
  PASSWORD: [
    {
      pattern: { test: (password) => password.length >= 8 },
      suffix: "Password not long enough",
      message: "La contraseña tiene menos de 8 caracteres",
    },
    {
      pattern: /[A-Z]/,
      suffix: "Password must have uppercase characters",
      message: "La contraseña no tiene mayúsculas",
    },
    {
      pattern: /[a-z]/,
      suffix: "Password must have lowercase characters",
      message: "La contraseña no tiene minúsculas",
    },
    {
      pattern: /\d/,
      suffix: "Password must have numeric characters",
      message: "La contraseña no tiene caracteres numéricos",
    },
  ],
};

const ERRORS = {
  FIELDS: {
    NEW_PWD: 0,
    CONFIRM_PWD: 1,
    ALL_PWD: 3,
  },
  LIMIT_EXCEEDED: {
    code: "LimitExceededException",
    message:
      "Límite de intentos excedido, por favor espere unos minutos y vuélvalo a intentar",
  },
  CODE_MISMATCH: {
    code: "CodeMismatchException",
    message: "Código de verificación incorrecto",
  },
  PASSWORD_MISMATCH: {
    message: "Las contraseñas no coinciden",
  },
};

function ForgotPassword(props: any) {
  const history = useHistory();
  const [authStep, setAuthStep] = useState(
    AUTHENTICATION_STEPS.FORGOT_PWD_EMAIL
  );
  const [email, updateEmail] = useState(props.email || "");
  const [code, updateCode] = useState("");
  const [newPassword, updateNewPassword] = useState("");
  const [confirmPassword, updateConfirmPassword] = useState("");
  const [error, updateError] = useState<{ message: string; field?: number }>({
    message: "",
  });

  const onClickSendCode = () => {
    sendCode.execute({ email });
  };

  const sendCode = useAsyncExecutor(
    (params: any) => {
      updateError({ message: "" });
      const user = new CognitoUser({ Username: params.email, Pool: USER_POOL });
      return new Promise((resolve: any) => {
        user.forgotPassword({
          onSuccess: () => {},
          onFailure: (err: any) => {
            if (err.code === ERRORS.LIMIT_EXCEEDED.code) {
              updateError({ message: ERRORS.LIMIT_EXCEEDED.message });
            } else {
              updateError({ message: err.message });
            }
          },
          inputVerificationCode() {
            resolve();
          },
        });
      });
    },
    {
      processingType: "EXHAUST",
      onSuccess: () => {
        setAuthStep(AUTHENTICATION_STEPS.FORGOT_PWD_NEW);
        updateError({ message: "" });
      },
    }
  );

  const confirmPasswordAC = useAsyncExecutor(
    (params: any) => {
      updateError({ message: "" });
      return new Promise((resolve: any, reject: any) => {
        const { code, newPassword, email } = params;
        const user = new CognitoUser({
          Username: email,
          Pool: USER_POOL,
        });
        user.confirmPassword(code, newPassword, {
          onSuccess: () => {
            resolve();
          },
          onFailure: (err) => {
            reject(err);
          },
        });
      });
    },
    {
      processingType: "EXHAUST",
      onSuccess: () => {
        history.push("/guest/login");
      },
      onError: (err) => {
        let message = "";
        if (err.code === ERRORS.CODE_MISMATCH.code) {
          message = ERRORS.CODE_MISMATCH.message;
        } else {
          for (const validation of VALIDATIONS.PASSWORD) {
            if (err.message.includes(validation.suffix)) {
              message = validation.message;
              break;
            }
          }
        }
        message = message ? message : err.message;
        updateError({ message });
      },
    }
  );

  const onClickConfirmPassword = () => {
    confirmPasswordAC.execute({ email, code, newPassword });
  };

  const setCodeFocus = (newCode: any) => {
    if (!newCode) newCode = code;
    let boxes: any = document.getElementsByClassName("input-digit");
    const boxesLength = newCode.length;
    for (let i = 0; i < boxesLength; i++) {
      boxes[i].value = newCode.charAt(i);
    }

    if (boxesLength !== 6) boxes[boxesLength].focus();
  };

  const onChangeCode = (e: any) => {
    let newCode = (code + e.target.value).slice(0, 6);
    updateCode(newCode);
    setCodeFocus(newCode);
  };

  const onBackPress = (e: any) => {
    if (e.keyCode !== 8) return;
    let boxes: any = document.getElementsByClassName("input-digit");
    const boxesLength = code.length;

    if (boxesLength > 0) {
      let newCode = code.slice(0, -1);
      updateCode(newCode);
      boxes[boxesLength - 1].focus();
    }
  };

  const onEmailChange = (mail: any) => {
    const message =
      mail && !VALIDATIONS.EMAIL.pattern.test(mail)
        ? VALIDATIONS.EMAIL.message
        : "";
    updateError({ message });
    updateEmail(mail);
  };

  const getValidationMessage = (password: any, otherPassword: any) => {
    for (const validation of VALIDATIONS.PASSWORD) {
      if (!validation.pattern.test(password)) {
        return validation.message;
      }
    }
    if (password !== "" && otherPassword !== "" && password !== otherPassword) {
      return ERRORS.PASSWORD_MISMATCH.message;
    }
    return "";
  };

  const onPasswordChange = (password: any, isConfirm = false) => {
    let message: any = "";
    if (isConfirm) {
      message = password ? getValidationMessage(password, newPassword) : "";
      updateConfirmPassword(password);
    } else {
      message = password ? getValidationMessage(password, confirmPassword) : "";
      updateNewPassword(password);
    }
    const field = message
      ? message === ERRORS.PASSWORD_MISMATCH.message
        ? ERRORS.FIELDS.ALL_PWD
        : isConfirm
        ? ERRORS.FIELDS.CONFIRM_PWD
        : ERRORS.FIELDS.NEW_PWD
      : null;
    if (message !== error.message) {
      updateError({ message, field });
    }
  };

  const onEnter = (e: any) => {
    if (e.keyCode !== 13) return;
    onClickSendCode();
  };

  return (
    <Container>
      <StepContainer>
        <div
          className="back-button"
          onClick={() => history.push("/guest/login")}
        >
          {`< Volver al login`}
        </div>
        <div className="logo">
          <img src={TalmaLogo} alt="talma" width="75%" />
        </div>
        {authStep === AUTHENTICATION_STEPS.FORGOT_PWD_EMAIL ? (
          <EmailStep>
            <h1>Recuperar Contraseña</h1>
            <div className="input-email">
              <h3>Email</h3>
              <Input
                width="30rem"
                hasError={email !== "" && error.message}
                onChange={(e) => onEmailChange(e.target.value)}
                value={email}
                onKeyDown={onEnter}
              ></Input>
            </div>
            <MainButton
              onClick={onClickSendCode}
              disabled={!email || error.message}
            >
              {sendCode.isLoading && (
                <img
                  src={LoadingSpinner}
                  width="50"
                  height="50"
                  alt="Processing request"
                />
              )}
              Enviar código de recuperación
            </MainButton>
            {error.message ? (
              <div className="error-message">{error.message}</div>
            ) : (
              <div className="info-message">
                Te enviaremos un código para que puedas elegir una nueva
                contraseña.
              </div>
            )}
          </EmailStep>
        ) : (
          <NewPasswordStep>
            <h1>Ingresa el código y tu nueva contraseña</h1>
            <div>
              <h3>Código (6 dígitos)</h3>
              {generateSequentialArray(NUMBER_DIGITS_FORGOT_PASSWORD_CODE).map(
                (key) => {
                  return (
                    <Input
                      className="input-digit"
                      key={key}
                      width="5rem"
                      hasError={
                        confirmPasswordAC.isError &&
                        confirmPasswordAC.error.code ===
                          ERRORS.CODE_MISMATCH.code
                      }
                      onClick={() => {
                        setCodeFocus(null);
                      }}
                      onChange={onChangeCode}
                      onKeyDown={onBackPress}
                      type="number"
                      disableOutline="true"
                      value={code.charAt(key)}
                      autoComplete="new-password"
                    />
                  );
                }
              )}
            </div>
            <div className="input-password">
              <h3>Nueva contraseña</h3>
              <Input
                value={newPassword}
                width="30rem"
                type="password"
                onChange={(e: any) => onPasswordChange(e.target.value)}
                hasError={
                  newPassword !== "" &&
                  (error.field === ERRORS.FIELDS.NEW_PWD ||
                    error.field === ERRORS.FIELDS.ALL_PWD)
                }
                autoComplete="new-password"
              ></Input>
            </div>
            <div className="input-password-confirm">
              <h3>Confirmar nueva contraseña</h3>
              <Input
                value={confirmPassword}
                width="30rem"
                type="password"
                hasError={
                  confirmPassword !== "" &&
                  (error.field === ERRORS.FIELDS.CONFIRM_PWD ||
                    error.field === ERRORS.FIELDS.ALL_PWD)
                }
                onChange={(e: any) => onPasswordChange(e.target.value, true)}
                autoComplete="new-password"
              ></Input>
            </div>
            <MainButton
              onClick={onClickConfirmPassword}
              disabled={
                code.length !== 6 ||
                !newPassword ||
                !confirmPassword ||
                error.message
              }
            >
              {confirmPasswordAC.isLoading && (
                <img
                  src={LoadingSpinner}
                  width="50"
                  height="50"
                  alt="Processing request"
                />
              )}
              Reestablecer contraseña
            </MainButton>
            <div className="error-message">{error.message}</div>
          </NewPasswordStep>
        )}
      </StepContainer>
    </Container>
  );
}
export default ForgotPassword;
